import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import useDebounce from "~/shared/hooks/useDebounce";

import { getAddressGeoType } from "./addressGeoHierarchy";
import { fetchGeoPlaces } from "./fetchGeoPlaces";
import { resolveGeoParentId } from "./resolveGeoParentId";
import {
  AddressGeoPagination,
  AddressGeoPlacesResult,
  AddressGeoSelections,
  AddressGeoToken,
  GeoPlaceOption,
  PickTokens,
  TokenFetchState,
} from "./types";

const EMPTY_PAGINATION: AddressGeoPagination = {
  page: 0,
  total: 0,
  hasMore: false,
};

const createInitialTokenState = (): TokenFetchState => ({
  options: [],
  loading: false,
  error: null,
  pagination: EMPTY_PAGINATION,
});

const selectionsSignature = (selections: AddressGeoSelections) =>
  (
    [
      "country",
      "fo",
      "subject",
      "district",
      "city",
      "locality",
      "street",
      "house",
    ] as const
  )
    .map((token) => `${token}:${selections[token]?.id ?? ""}`)
    .join("|");

const buildEmptyResult = <T extends AddressGeoToken>(
  tokens: readonly T[],
): AddressGeoPlacesResult<T> => {
  const options = {} as Record<T, GeoPlaceOption[]>;
  const loading = {} as Record<T, boolean>;
  const error = {} as Record<T, string | null>;
  const pagination = {} as Record<T, AddressGeoPagination>;
  const loadMore = {} as Record<T, () => void>;
  const refetch = {} as Record<T, () => void>;

  tokens.forEach((token) => {
    options[token] = [];
    loading[token] = false;
    error[token] = null;
    pagination[token] = EMPTY_PAGINATION;
    loadMore[token] = () => undefined;
    refetch[token] = () => undefined;
  });

  return { options, loading, error, pagination, loadMore, refetch };
};

export function useAddressGeoPlaces<
  T extends readonly AddressGeoToken[],
>(params: {
  tokens: T;
  selections: AddressGeoSelections;
  pageSize?: number;
}): AddressGeoPlacesResult<PickTokens<T>> {
  const { tokens, selections, pageSize = 10000 } = params;
  const tokensKey = tokens.join(",");
  const [state, setState] = useState<
    Partial<Record<AddressGeoToken, TokenFetchState>>
  >({});
  const abortControllersRef = useRef<
    Partial<Record<AddressGeoToken, AbortController>>
  >({});
  const stateRef = useRef(state);

  stateRef.current = state;

  const signature = useMemo(
    () => selectionsSignature(selections),
    [selections],
  );
  const debouncedSignature = useDebounce(signature, 200);
  const debouncedSelections = useMemo(() => selections, [debouncedSignature]);

  const fetchForToken = useCallback(
    async (token: AddressGeoToken, page: number, append: boolean) => {
      abortControllersRef.current[token]?.abort();
      const controller = new AbortController();
      abortControllersRef.current[token] = controller;

      if (token !== "country") {
        const parentId = resolveGeoParentId(token, debouncedSelections);

        if (!parentId) {
          setState((prev) => ({
            ...prev,
            [token]: createInitialTokenState(),
          }));
          return;
        }
      }

      setState((prev) => ({
        ...prev,
        [token]: {
          ...(prev[token] ?? createInitialTokenState()),
          loading: true,
          error: null,
        },
      }));

      try {
        const parentId =
          token === "country"
            ? undefined
            : resolveGeoParentId(token, debouncedSelections);

        const result = await fetchGeoPlaces({
          type: getAddressGeoType(token),
          count: pageSize,
          ...(token === "country" ? {} : { parentId }),
          ...(page > 0 ? { page } : {}),
          signal: controller.signal,
        });

        if (controller.signal.aborted) {
          return;
        }

        setState((prev) => {
          const prevToken = prev[token] ?? createInitialTokenState();

          return {
            ...prev,
            [token]: {
              options: append
                ? [...prevToken.options, ...result.items]
                : result.items,
              loading: false,
              error: null,
              pagination: {
                page: result.page,
                total: result.total,
                hasMore: result.hasMore,
              },
            },
          };
        });
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }

        setState((prev) => ({
          ...prev,
          [token]: {
            ...(prev[token] ?? createInitialTokenState()),
            loading: false,
            error: err instanceof Error ? err.message : String(err),
          },
        }));
      }
    },
    [debouncedSelections, pageSize],
  );

  useEffect(() => {
    tokens.forEach((token) => {
      fetchForToken(token, 0, false);
    });

    return () => {
      tokens.forEach((token) => {
        abortControllersRef.current[token]?.abort();
      });
    };
  }, [tokensKey, debouncedSignature, fetchForToken, tokens]);

  const loadMoreHandlers = useMemo(() => {
    const handlers = {} as Record<PickTokens<T>, () => void>;

    tokens.forEach((token) => {
      handlers[token as PickTokens<T>] = () => {
        const tokenState = stateRef.current[token];

        if (!tokenState?.pagination.hasMore || tokenState.loading) {
          return;
        }

        fetchForToken(token, tokenState.pagination.page + 1, true);
      };
    });

    return handlers;
  }, [fetchForToken, tokensKey, tokens]);

  const refetchHandlers = useMemo(() => {
    const handlers = {} as Record<PickTokens<T>, () => void>;

    tokens.forEach((token) => {
      handlers[token as PickTokens<T>] = () => {
        fetchForToken(token, 0, false);
      };
    });

    return handlers;
  }, [fetchForToken, tokensKey, tokens]);

  return useMemo(() => {
    const result = buildEmptyResult(tokens as readonly PickTokens<T>[]);

    tokens.forEach((token) => {
      const tokenState = state[token] ?? createInitialTokenState();

      result.options[token as PickTokens<T>] = tokenState.options;
      result.loading[token as PickTokens<T>] = tokenState.loading;
      result.error[token as PickTokens<T>] = tokenState.error;
      result.pagination[token as PickTokens<T>] = tokenState.pagination;
      result.loadMore[token as PickTokens<T>] =
        loadMoreHandlers[token as PickTokens<T>];
      result.refetch[token as PickTokens<T>] =
        refetchHandlers[token as PickTokens<T>];
    });

    return result;
  }, [loadMoreHandlers, refetchHandlers, state, tokens, tokensKey]);
}
